<template>
    <hh-form class="plan-notes-form">
        <hh-form-group v-for="note in notes"
                       :key="note.key"
                       class="plan-notes-form__group plan-notes-form__group--textarea">
            <hh-label :for-def="note.key"
                      class="plan-notes-form__label"
                      :class="{
                          'plan-notes-form__label--post': note.displayType === 'checkbox',
                          'plan-notes-form__label--pre': note.displayType !== 'checkbox',
                      }"
                      tabstop>
                <hh-checkbox v-if="note.displayType === 'checkbox'"
                             :def="note.key"
                             :tabstop="false"
                             :class="`plan-notes-form__checkbox plan-notes-form__${note.key}`"
                             :value="getBooleanNoteValue(note.key)"
                             @input="setNoteAsync(note, $event)" />
                {{ note.label }}
            </hh-label>
            <hh-textbox v-if="note.displayType === 'textarea'"
                        :def="note.key"
                        is-area
                        :class="`plan-notes-form__textarea plan-notes-form__${note.key}`"
                        :value="getNoteValue(note.key)"
                        @input="setNoteValueAsync(note.key, $event)" />
            <hh-textbox v-if="note.hasAdditionalInfo && getBooleanNoteValue(note.key)"
                        :def="note.additionalInfoKey"
                        is-area
                        :class="`plan-notes-form__textarea plan-notes-form__${note.additionalInfoKey}`"
                        :value="getNoteValue(note.additionalInfoKey)"
                        @input="setNoteValueAsync(note.additionalInfoKey, $event)" />
        </hh-form-group>
    </hh-form>
</template>

<script>

import Vuex from 'vuex'

import PlanBuilderManager from '@/plan-builder-manager'
import Project from '@/models/project'

export default {
    computed: {

        ...Vuex.mapState({
            /**
             * @param {VuexState} stateStore
             * @returns {Project}
             */
            currentProject: stateStore => Project.ensure(stateStore.improvements.currentProject),
        }),

        /**
         * @returns {Array<Note>}
         */
        notes() {
            return this.$scheme.notes
        },
    },

    methods: {
        /**
         * Get the note value of the current project for the given key. Convert
         * it to a boolean value of true if Y, otherwise false, and return it.
         * @param {string} key
         * @returns {boolean}
         */
        getBooleanNoteValue(key) {
            const note = this.getNoteValue(key)

            return note && note === 'Y'
        },

        /**
         * Take a boolean value from a checkbox and convert it to a string, Y if true, otherwise N.
         * Update the note's key and value on the notes of the current project.
         * @params {Note} note
         * @params {boolean} value
         */
        async setNoteAsync(note, value) {
            this.$logger.trackMethod('setNoteAsync', 'plan-notes.vue', { key: note.key, value })

            await PlanBuilderManager.setNoteAsync(note.key, value ? 'Y' : 'N')

            // this note has additional info and its now set to false so
            // clear the additional info key
            if (note.hasAdditionalInfo && !value)
                await this.setNoteValueAsync(note.additionalInfoKey, null)
        },

        /**
         * Get the note value of the current project for the given key.
         * @param {string} key The key for the note.
         * @returns {?string} The note value.
         */
        getNoteValue(key) {
            this.$logger.trackMethod('getNoteValue', 'plan-notes-form.vue', { key })

            const notes = this.currentProject && this.currentProject.notes ? this.currentProject.notes : []
            const note = notes.find(n => n.key === key)

            if (note === undefined || note == null) return null

            return note.value
        },

        /**
         * Update the key and value on the notes of the current project.
         * @params {string} key
         * @params {boolean} value
         */
        async setNoteValueAsync(key, value) {
            this.$logger.trackMethod('setNoteValueAsync', 'plan-notes.vue', { key, value })

            await PlanBuilderManager.setNoteAsync(key, value)
        },
    },
}

</script>

<style lang="scss">

// Ensure colours and other visual style elements are added to theme files, not placed in here.
// src\scss\schemes\components\plan-notes\_plan-notes-form.scss

.plan-notes-form {

    @include element('group') {

        @include modifier('textarea') {

            @include bp-min-small {
                display: block !important;
            }
        }
    }

    @include element('label') {
        @include user-select-none;

        @include modifier('post') {

            label {
                margin-right: 0 !important;
            }
        }
    }

    @include element('textarea') {

        @include bp-min-wide {
            max-width: 560px;
        }
    }
}

</style>
